import React, { useEffect, useState, useCallback, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'

import { AdasBox, AdasButton, AdasContainer, AdasDrawer, AdasPaper, AdasTabs, AdasTypography } from '@components/wrapper-components'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { ProductForm, ProductInfo, ProductList, ProductProfileSelector } from '@components/product'
import { Logger } from '../../logger'
import {
  fetchPartsList,
  fetchServiceList,
  fetchCalibrationList,
  fetchLabourList,
  getCalibrationList,
  getLabourList,
  getServiceList,
  getPartsList
} from '../../api/api'
import theme from 'theme/theme'
import { useUserStore } from '@caradasstore/UserStore'
import { ImportCSVComponent } from '@components/import-form'
import { uploadProductCSV, validateProductCSV } from '../../api/api'
import { GENERIC_MESSAGES } from 'constants'
import { downloadAsFile } from 'utils/utils'
import { PRODUCT_LIST_HEADERS, SERVICE_LIST_HEADERS, PARTS_LIST_HEADERS, LABOUR_LIST_HEADERS } from 'constants'

const IMPORT_CLIENT_STEPPER = [0, 1]

export const ProductsPage = () => {
  const [calibrationList, setCalibrationList] = useState([])
  const [serviceList, setServiceList] = useState([])
  const [partsList, setPartsList] = useState([])
  const [labourList, setLabourList] = useState([])
  const [currentTab, setCurrentTab] = React.useState('calibration')
  const [isProductInfo, setIsProductInfo] = useState(false)
  const [isAddProduct, setIsAddProduct] = useState(false)
  const [productData, setProductData] = useState({})
  const ccId = useCenterStore((store) => store.ccId)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const centerDetail = useCenterStore((store) => store.centerDetail)

  const [openImportForm, setOpenImportForm] = useState(false)
  const [activeStep, setActiveStep] = useState(IMPORT_CLIENT_STEPPER[0])
  const [file, setFile] = useState(null)
  const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState(null)
  const [csvValid, setCsvValid] = useState(true)
  const [CSVValidationResponse, setCSVValidationResponse] = useState(null)
  const [CSVUploadResponse, setCSVUploadResponse] = useState(null)
  const [selectedReferralId, setSelectedReferralId] = useState('')
  const [selectedProfileType, setSelectedProfileType] = useState('calibration')
  const [pagination, setPagination] = useState({
    calibration: { pageNo: 0, pageSize: 25, totalPages: 0, totalCount: 0, search: '' },
    service: { pageNo: 0, pageSize: 25, totalPages: 0, totalCount: 0, search: '' },
    part: { pageNo: 0, pageSize: 25, totalPages: 0, totalCount: 0, search: '' },
    labor: { pageNo: 0, pageSize: 25, totalPages: 0, totalCount: 0, search: '' }
  })

  const [searchInput, setSearchInput] = useState('')

  const allTabs = [
    { label: 'Calibrations', value: 'calibration' },
    { label: 'Services', value: 'service' },
    { label: 'Parts', value: 'part' },
    { label: 'Labor', value: 'labor' }
  ]

  // Filter tabs based on isToggled
  const tabs = allTabs.filter((tab) => {
    return true // Include all other tabs
  })

  const user = useUserStore((store) => store.currentUser)
  const requiredFlags = [
    'is_sf_enabled',
    'is_pgw_enabled',
    'is_enterprise_enabled',
    'is_geico_enabled',
    'is_allstate_enabled',
    'is_bearriver_enabled'
  ]

  const shouldShowProductProfile = requiredFlags.some((flag) => centerDetail?.[flag])

  const fetchAndSetList = async (listFetcher, setter, tabKey) => {
    try {
      setLoading(true)
      const currentParams = pagination[tabKey]
      const response = await listFetcher({
        pageNo: currentParams.pageNo,
        pageSize: currentParams.pageSize,
        status: currentParams.status,
        ccid: ccId,
        search: currentParams.search,
        ...(selectedReferralId && { belongsTo: 'REFERRAL', belongsToId: selectedReferralId }) // ✅ include if available
      })

      const data = response.data
      setter(data.calibrations || data.services || data.parts || data.labours)

      setPagination((prev) => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          totalPages: data.totalPages || 0,
          totalCount: data.totalCount || 0
        }
      }))
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'fetchAndSetList', method: listFetcher.name, ccId } })
    } finally {
      setLoading(false)
    }
  }
  const fetchServices = () => fetchAndSetList(fetchServiceList, setServiceList, 'service')
  const fetchCalibrations = () => fetchAndSetList(fetchCalibrationList, setCalibrationList, 'calibration')
  const fetchParts = () => fetchAndSetList(fetchPartsList, setPartsList, 'part')
  const fetchLabour = () => fetchAndSetList(fetchLabourList, setLabourList, 'labor')

  const changeTab = (event, newValue) => {
    setCurrentTab(newValue)
    setSearchInput('') // Clear the search input
    setPagination((prev) => ({
      ...prev,
      [newValue]: {
        ...prev[newValue],
        search: '',
        pageNo: 0
      }
    }))
  }

  const selectProduct = (product) => {
    setProductData(product)
    setIsProductInfo(true)
  }

  const updateProductList = () => {
    const fetchProductList = {
      calibration: fetchCalibrations,
      service: fetchServices,
      part: fetchParts,
      labor: fetchLabour
    }

    if (fetchProductList[currentTab]) {
      fetchProductList[currentTab]() // Fetch data for the current tab
    }

    setIsAddProduct(false)
  }
  useEffect(() => {
    const performSearch = () => {
      setPagination((prev) => ({
        ...prev,
        [currentTab]: {
          ...prev[currentTab],
          search: searchInput,
          pageNo: 0 // Reset to first page on new search
        }
      }))
    }

    const timeoutId = setTimeout(performSearch, 1000)
    return () => clearTimeout(timeoutId)
  }, [searchInput, currentTab])

  const fetchData = async () => {
    try {
      setLoading(true)
      const fetchMap = {
        calibration: fetchCalibrations,
        service: fetchServices,
        part: fetchParts,
        labor: fetchLabour
      }

      if (fetchMap[currentTab]) {
        await fetchMap[currentTab]()
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductsPage' } })
    } finally {
      setLoading(false)
    }
  }

  const fetchAllItems = async (fetcher, type) => {
    try {
      const data = await fetcher({
        ccid: ccId,
        ...(selectedReferralId && { belongsTo: 'REFERRAL', belongsToId: selectedReferralId }) // ✅ include if available
      })
      return data
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductsPage', method: `fetchAllItems-${type}` } })
      return []
    }
  }

  const downloadProducts = useCallback(
    async ({ template = false } = {}) => {
      setLoading(true)
      try {
        let allCalibrations = []
        let allServices = []
        let allParts = []
        let allLabours = []

        if (!template) {
          // Fetch all data without pagination
          allCalibrations = await fetchAllItems(getCalibrationList, 'calibration')
          allServices = await fetchAllItems(getServiceList, 'service')
          allParts = await fetchAllItems(getPartsList, 'part')
          allLabours = await fetchAllItems(getLabourList, 'labour')
        }

        let exportData = template
          ? []
          : [
              ...allCalibrations.map((product) => [
                `"${product.name}"`,
                'Calibration',
                `"${product.category?.name || ''}"`,
                `"${product.price}"`,
                `"${product.is_active}"`,
                product.description ? `"${product.description}"` : '',
                product.is_defaulted ? `"${product.is_defaulted}"` : 'false',
                '\n'
              ]),
              ...allServices.map((service) => [
                `"${service.name}"`,
                'Service',
                `"${service.category?.name || ''}"`,
                `"${service.price}"`,
                `"${service.is_active}"`,
                service.description ? `"${service.description}"` : '',
                service.is_defaulted ? `"${service.is_defaulted}"` : 'false',
                '\n'
              ]),
              ...allParts.map((part) => [
                `"${part.name}"`,
                'Part',
                `"${part.category?.name || ''}"`,
                `"${part.price}"`,
                `"${part.is_active}"`,
                part.description ? `"${part.description}"` : '',
                part.is_defaulted ? `"${part.is_defaulted}"` : 'false',
                '\n'
              ]),
              ...allLabours.map((labour) => [
                `"${labour.name}"`,
                'Labor',
                `"${labour.category?.name || ''}"`,
                `"${labour.price}"`,
                `"${labour.is_active}"`,
                labour.description ? `"${labour.description}"` : '',
                labour.is_defaulted ? `"${labour.is_defaulted}"` : 'false',
                '\n'
              ])
            ]

        exportData = [
          ['Product Name', 'Type', 'Category Name', 'Product Price', 'Status', 'Product Description', 'Default Product\n'],
          ...exportData
        ]

        const fileName = 'product_service_list.csv'
        const blob = new Blob(exportData, { type: 'text/plain' })
        downloadAsFile(blob, fileName)
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'ProductsPage', method: 'downloadProducts' } })
      } finally {
        setLoading(false)
      }
    },
    [ccId, selectedReferralId]
  )

  const handleValidateCSV = useCallback(async () => {
    if (file instanceof File) {
      const formData = new FormData()
      formData.append('file', file, file.name)
      try {
        const response = await validateProductCSV({ file: formData, ccid: ccId })
        if (response.data.status === 'error') {
          setCsvValid(false)
        } else {
          setCsvValid(true)
        }
        setCSVValidationResponse(response.data.messages)
        setActiveStep((step) => step + 1)
      } catch (error) {
        if (error.status === 400) {
          setCSVValidationResponse(error.response.data.messages)
          setCsvValid(false)
          setActiveStep((step) => step + 1)
        } else {
          Logger.error({ message: error, payload: { file: 'ProductListPage', method: 'handleValidateCSV' } })
        }
      }
    }
  }, [file, validateProductCSV, setActiveStep, setCSVValidationResponse, setCsvValid, ccId])

  const handleCSVUpload = useCallback(async () => {
    if (file instanceof File) {
      const formData = new FormData()
      formData.append('file', file, file.name)
      const response = await uploadProductCSV({ file: formData, ccid: ccId })
      setCSVUploadResponse(response.data.messages)
      setActiveStep((step) => step + 1)
      fetchData()
    }
  }, [file, uploadProductCSV, setActiveStep, ccId])
  const handleClose = useCallback(() => {
    setFile(null)
    setFileUploadErrorMessage(null)
    setActiveStep(IMPORT_CLIENT_STEPPER[0])
    setCSVValidationResponse(null)
    setOpenImportForm(false)
    setCsvValid(true)
  }, [])
  const paginationHandler = useCallback(
    (event, page) => {
      setPagination((prev) => ({
        ...prev,
        [currentTab]: {
          ...prev[currentTab],
          pageNo: page - 1
        }
      }))
    },
    [currentTab]
  )

  const pageSizeHandler = useCallback(
    (event) => {
      setPagination((prev) => ({
        ...prev,
        [currentTab]: {
          ...prev[currentTab],
          pageSize: event.target.value,
          pageNo: 0
        }
      }))
    },
    [currentTab]
  )
  const hasFetchedInitially = useRef(false)
  const clearListForTab = (tab) => {
    switch (tab) {
      case 'calibration':
        setCalibrationList([])
        break
      case 'service':
        setServiceList([])
        break
      case 'part':
        setPartsList([])
        break
      case 'labor':
        setLabourList([])
        break
      default:
        break
    }
  }

  useEffect(() => {
    const isNationalWithoutReferral = selectedProfileType === 'national' && !selectedReferralId

    if (isNationalWithoutReferral) {
      clearListForTab(currentTab)
      return // 🛑 Stop further API calls
    }

    if (!hasFetchedInitially.current) {
      hasFetchedInitially.current = true
    } else {
      clearListForTab(currentTab)
    }

    const fetchMap = {
      calibration: fetchCalibrations,
      service: fetchServices,
      part: fetchParts,
      labor: fetchLabour
    }

    const fetchFn = fetchMap[currentTab]
    if (fetchFn) fetchFn()
  }, [
    currentTab,
    pagination[currentTab].pageNo,
    pagination[currentTab].pageSize,
    pagination[currentTab].search,
    ccId,
    selectedReferralId,
    selectedProfileType
  ])

  return (
    <AdasContainer maxWidth='lg' sx={{ paddingBottom: '10px' }}>
      {isProductInfo && (
        <AdasDrawer anchor={'right'} open={isProductInfo} onClose={() => setIsProductInfo(false)}>
          <AdasBox>
            <ProductInfo
              product={productData}
              productTypeName={currentTab}
              closeDrawer={() => setIsProductInfo(false)}
              onSaveSuccess={updateProductList}
              selectedProfileType={selectedProfileType}
            />
          </AdasBox>
        </AdasDrawer>
      )}
      {isAddProduct && (
        <AdasDrawer anchor={'right'} open={isAddProduct} onClose={() => setIsAddProduct(false)}>
          <AdasBox className='drawer-title-container'>
            <AdasTypography variant='h6' component='span'>
              Product Info
            </AdasTypography>
            <AdasButton
              buttonType='iconButton'
              sx={{
                padding: '2px',
                cursor: 'pointer',
                color: theme.palette.info.main
              }}
              onClick={() => setIsAddProduct(false)}
            >
              <CloseIcon fontSize='small' />
            </AdasButton>
          </AdasBox>
          <ProductForm
            productData={{ calibration_center_id: ccId }}
            productTypeName={currentTab}
            onSaveSuccess={updateProductList}
            close={() => setIsAddProduct(false)}
          />
        </AdasDrawer>
      )}
      <AdasPaper
        sx={{
          padding: '20px 20px',
          backgroundColor: 'white',
          marginTop: '20px'
        }}
      >
        <ProductProfileSelector
          onCategorySelect={({ referralId, profile }) => {
            setSelectedReferralId(referralId)
            setSelectedProfileType(profile)
            setCurrentTab('calibration')
          }}
          shouldShowProductProfile={shouldShowProductProfile}
          selectedProfileType={selectedProfileType}
        />

        <AdasBox display='flex' justifyContent='space-between' flexDirection={{ xs: 'column', md: 'row' }} gap={2}>
          <AdasTabs tabs={tabs} value={currentTab} onChange={changeTab} aria-label='product listing tab set' />
          {user?.is_admin && (
            <>
              {selectedProfileType !== 'national' && (
                <AdasBox
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center'
                  }}
                >
                  <AdasButton
                    variant='contained'
                    color='primary'
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={() => setOpenImportForm(true)}
                  >
                    Import CSV
                  </AdasButton>
                  <AdasButton
                    variant='outlined'
                    aria-label='Export CSV'
                    color='primary'
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={downloadProducts}
                  >
                    Export CSV
                  </AdasButton>
                </AdasBox>
              )}

              {selectedProfileType === 'national' && selectedReferralId && (
                <AdasBox
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center'
                  }}
                >
                  <AdasButton
                    variant='outlined'
                    aria-label='Export CSV'
                    color='primary'
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    onClick={downloadProducts}
                  >
                    Export CSV
                  </AdasButton>
                </AdasBox>
              )}
            </>
          )}
        </AdasBox>
        {selectedProfileType === 'national' && !selectedReferralId ? (
          <AdasBox display='flex' alignItems='center' justifyContent='center' minHeight='200px'>
            Please select a Referral Source to view National Agreement products.
          </AdasBox>
        ) : (
          <>
            {currentTab === 'calibration' && (
              <ProductList
                products={calibrationList}
                onAddProduct={setIsAddProduct}
                onSelectProduct={selectProduct}
                columns={PRODUCT_LIST_HEADERS}
                pageSizeHandler={pageSizeHandler}
                paginationHandler={paginationHandler}
                pageParams={pagination[currentTab]}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                selectedProfileType={selectedProfileType}
              />
            )}
            {currentTab === 'service' && (
              <ProductList
                products={serviceList}
                onAddProduct={setIsAddProduct}
                onSelectProduct={selectProduct}
                columns={SERVICE_LIST_HEADERS}
                pageSizeHandler={pageSizeHandler}
                paginationHandler={paginationHandler}
                pageParams={pagination[currentTab]}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                selectedProfileType={selectedProfileType}
              />
            )}
            {currentTab === 'part' && (
              <ProductList
                products={partsList}
                onAddProduct={setIsAddProduct}
                onSelectProduct={selectProduct}
                columns={PARTS_LIST_HEADERS}
                pageSizeHandler={pageSizeHandler}
                paginationHandler={paginationHandler}
                pageParams={pagination[currentTab]}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                selectedProfileType={selectedProfileType}
              />
            )}
            {currentTab === 'labor' && (
              <ProductList
                products={labourList}
                onAddProduct={setIsAddProduct}
                onSelectProduct={selectProduct}
                columns={LABOUR_LIST_HEADERS}
                pageSizeHandler={pageSizeHandler}
                paginationHandler={paginationHandler}
                pageParams={pagination[currentTab]}
                searchInput={searchInput}
                setSearchInput={setSearchInput}
                selectedProfileType={selectedProfileType}
              />
            )}
          </>
        )}
      </AdasPaper>

      <ImportCSVComponent
        open={openImportForm}
        title='Import Products'
        onClose={handleClose}
        downloadCSV={downloadProducts}
        handleValidateCSV={handleValidateCSV}
        handleCSVUpload={handleCSVUpload}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        csvValid={csvValid}
        CSVValidationResponse={CSVValidationResponse}
        file={file}
        IMPORT_CLIENT_STEPPER={IMPORT_CLIENT_STEPPER}
        GENERIC_MESSAGES={GENERIC_MESSAGES}
        CSVUploadResponse={CSVUploadResponse}
        setCsvValid={setCsvValid}
        setFile={setFile}
        fileUploadErrorMessage={fileUploadErrorMessage}
        setFileUploadErrorMessage={setFileUploadErrorMessage}
      />
    </AdasContainer>
  )
}
